/**
 *
 * SpotShipmentPage
 *
 */

import React, { Suspense } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { injectIntl } from "react-intl";
import identity from "lodash/fp/identity";
import { IntlPropType, AutoComplete, FormField } from "root/libs/core-libs/src";
import { CountrySettings } from "root/libs/ui-containers/src";
import { selectTrackingKey } from "one-time-shipment/src/containers/Tracking/selectors";
import ShipmentLane from "one-time-shipment/src/containers/ShipmentLane";
import SegmentControl from "one-time-shipment/src/components/Forms/SegmentControl";
import BypassBlock from "one-time-shipment/src/components/Buttons/BypassBlock";
import ModalShipmentInformationDetected from "one-time-shipment/src/components/Modals/ModalShipmentInformationDetected";
import TradeLane from "one-time-shipment/src/containers/TradeLane";
import SkeletonOr from "one-time-shipment/src/containers/SkeletonOr";
import globalConfiguration from "one-time-shipment/src/config/globalConfiguration";
import { SEGMENT_TYPES } from "one-time-shipment/src/containers/SpotShipmentPage/constants";
import {
  keys,
  COMMON,
  UTF_EVENT_PAGE_NAMES,
  UTF_EVENT_CONTENT_NAMES,
  UTF_EVENT_TYPES,
  UTF_EVENT_INTERACTIONS,
  UTF_EVENT_CONTENT_POSITION,
} from "root/libs/constants/src";
import { countryAutocompleteFieldMessages } from "root/libs/ui-components/src";
import {
  abTestVersions,
  STEPPER,
} from "root/libs/ui-containers/src/lib/AbTest";
import OffersLane from "one-time-shipment/src/containers/OffersLane";
import checkIcon from "icons/cx/check.svg";
import ResponsiveRender from "one-time-shipment/src/components/Layout/ResponsiveRender";
import {
  BypassBlockWrapper,
  GlobalPrintStyle,
  ProductChip,
  StyledHeadline,
  OverAllWrapper,
  StepperIndicator,
} from "./styled";
import van from "../../components/ShipmentItem/ShipmentLaneComponent/images/van.svg";
import truck from "../../components/ShipmentItem/ShipmentLaneComponent/images/truck.svg";
import illus from "../../components/ShipmentItem/ShipmentLaneComponent/images/illus.svg";
import stepNumber from "../../components/ShipmentItem/ShipmentLaneComponent/images/step-indicator.svg";
import summary from "../../components/ShipmentItem/ShipmentLaneComponent/images/summary.svg";
import StepperCard from "../../components/Stepper/StepperCard";

const { withCountry } = CountrySettings;

const COPY_ID_PREFIX = "SpotShipmentPage";

const COPY_ID_PREFIX_STEPPER = "Stepper";

const COPY_ID_PREFIX_SHIPMENT_SUMMARY = "ShipmentSummary";

class SpotShipment extends React.PureComponent {
  static propTypes = {
    intl: IntlPropType.isRequired,
    language: PropTypes.string,
    country: PropTypes.string,
    isByPassBlockVisible: PropTypes.bool,
    onSegmentChange: PropTypes.func,
    selectedSegment: PropTypes.string.isRequired,
    segmentReference: PropTypes.object,
    /** function to add values to the digitalLayer for tracking purposes.
     * Receives an object, the key is the property that will recieve the data in the digitalLayer
     * and the value is the data that it needs */
    trackEvent: PropTypes.func,
    showShipmentDetectedModal: PropTypes.bool,
    closeShipmentDetectedModal: PropTypes.func.isRequired,
    abTestRunning: PropTypes.bool.isRequired,
    abTestVersion: PropTypes.string,
    canaryEnabled: PropTypes.bool.isRequired,
    canaryVersion: PropTypes.string,
    hideSegments: PropTypes.bool,
    segments: PropTypes.array,
    originFields: PropTypes.object,
    destinationFields: PropTypes.object,
    productItems: PropTypes.array,
    unitSystem: PropTypes.object.isRequired,
    selectedUnitSystem: PropTypes.string,
  };

  static defaultProps = {
    language: "en",
    country: "NL",
    isByPassBlockVisible: false,
    onSegmentChange: () => {},
    trackEvent: () => {},
    segmentReference: React.createRef(),
    showShipmentDetectedModal: false,
    hideSegments: false,
    segments: [SEGMENT_TYPES.private, SEGMENT_TYPES.business],
    destinationFields: {},
    productItems: [],
  };

  constructor(props) {
    super(props);

    const { segments, originFields } = props;
    this.TradeLaneWithCountry = withCountry(TradeLane);
    this.ShipmentLaneWithCountry = withCountry(ShipmentLane);
    this.OffersLaneWithCountry = withCountry(OffersLane);
    const trackingKeySelector = selectTrackingKey;
    this.selectedSegmentTrackingKeyPath = trackingKeySelector(
      "spotShipment.selectedSegment"
    );

    this.segments = segments.map((s) => {
      return {
        id: SEGMENT_TYPES[s],
        headlineString: this.getCopy(`${s}Shipment`),
      };
    });

    this.OffersLaneReference = React.createRef();
    this.backToTradeLaneReference = React.createRef();
    this.backToShipmentLaneReference = React.createRef();

    this.state = {
      currentPage: "TradeLane",
      importToggleEnabled: false,
    };
    this.originFields = originFields;
  }

  scrollToDimensionContent = (index) => {
    setTimeout(() => {
      const dimensionContent = document.querySelector(
        `[data-testid="dimensions-content-card-${index + 1}"]`
      );
      if (dimensionContent) {
        dimensionContent.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    }, 500);
  };

  getCountryValue = (countryField) => {
    const { intl } = this.props;
    return AutoComplete.case(
      {
        unselected: identity,
        selected: (value) =>
          intl.formatMessage(countryAutocompleteFieldMessages[value]),
      },
      FormField.getValue(countryField)
    );
  };

  // Back button handler for ShipmentLane
  handleBack = () => {
    this.setState((prevState) => {
      if (prevState.currentPage === "ShipmentLane") {
        return { currentPage: "TradeLane" };
      }
      if (prevState.currentPage === "OffersLane") {
        return { currentPage: "ShipmentLane" };
      }
      // Fallback return to satisfy ESLint
      return null;
    });
  };

  moveToStep = (step) => {
    const stepMap = {
      1: "TradeLane",
      2: "ShipmentLane",
      3: "OffersLane",
    };

    const page = stepMap[step];

    if (!page) {
      return;
    }

    this.setState({ currentPage: page });
  };

  getCopy = (id, values, prefix = COPY_ID_PREFIX) => {
    const { intl } = this.props;
    return intl.formatMessage({ id: `${prefix}.${id}` }, values);
  };

  onModalShipmentInformationDetectedModalClose = () => {
    const { closeShipmentDetectedModal, country } = this.props;
    closeShipmentDetectedModal({ country });
  };

  onRestoreHandlerShipmentDetectedModal = () => {
    // Restore state here
    const { closeShipmentDetectedModal, country } = this.props;
    const localStorageSavedShipment = window.localStorage.getItem(
      `${COMMON.SAVE_SHIPMENT_LOCAL_STORAGE}:${country}`
    );
    closeShipmentDetectedModal({ localStorageSavedShipment, country });
  };

  render() {
    const {
      intl,
      language,
      country,
      isByPassBlockVisible,
      onSegmentChange,
      selectedSegment,
      segmentReference,
      hideSegments,
      trackEvent,
      showShipmentDetectedModal,
      abTestRunning,
      abTestVersion,
      canaryEnabled,
      canaryVersion,
      originFields,
      destinationFields,
      productItems,
      unitSystem,
      selectedUnitSystem,
    } = this.props;

    const isBusiness = selectedSegment === SEGMENT_TYPES.business;

    const stepperEnabled =
      abTestRunning && abTestVersion === abTestVersions[STEPPER];

    const { currentPage, importToggleEnabled } = this.state;

    const totalSteps = 3;

    const ariaDescription = {
      1: "Please choose business or private tab and enter your shipment destination",
      2: "Enter you item's weight and dimensions or add multiple items",
      3: "Review your delivery options and book now",
    };

    const getCurrentStep = () => {
      if (currentPage === "TradeLane") {
        return 1;
      }
      if (currentPage === "ShipmentLane") {
        return 2;
      }

      return 3;
    };

    const currentStep = getCurrentStep();

    const steps = [
      {
        id: 1,
        label: this.getCopy(
          "shippingLocation",
          undefined,
          COPY_ID_PREFIX_STEPPER
        ),
      },
      {
        id: 2,
        label: this.getCopy(
          "shipmentDetails",
          undefined,
          COPY_ID_PREFIX_STEPPER
        ),
      },
      {
        id: 3,
        label: this.getCopy("bookNow", undefined, COPY_ID_PREFIX_STEPPER),
      },
    ];

    const getIllustration = () => {
      switch (currentStep) {
        case 1:
          return van;
        case 2:
          return truck;
        case 3:
          return illus;
        default:
          return van;
      }
    };

    const getStepStatus = (stepId) => {
      if (stepId < currentStep) return "completed";
      if (stepId === currentStep) return "active";
      return "pending";
    };

    const formatProductDetails = (product, index) => {
      const { weight, height, length, width } = product;

      if (!weight || !length || !width || !height) {
        return null;
      }

      const dimensions = `${length} x ${width} x ${height} ${unitSystem[selectedUnitSystem].length}`;

      return (
        <ProductChip
          tabIndex={0}
          key={index}
          onKeyUp={(e) => {
            const { code } = e;
            if (code === keys.SPACE || code === keys.RETURN) {
              if (currentStep > 2) {
                this.moveToStep(2);
              }
              // Scroll to the specific dimension content after a small delay to ensure component is mounted
              this.scrollToDimensionContent(index);
            }
          }}
          onClick={() => {
            if (currentStep > 2) {
              this.moveToStep(2);
            }
            // Scroll to the specific dimension content after a small delay to ensure component is mounted
            this.scrollToDimensionContent(index);
          }}
        >
          <strong>#{index + 1}</strong>&nbsp;{weight}
          {unitSystem[selectedUnitSystem].weight}, {dimensions}
        </ProductChip>
      );
    };

    return (
      <React.Fragment>
        {canaryEnabled && (
          <Helmet>
            <meta name="canaryVersion" content={canaryVersion} />
          </Helmet>
        )}
        {showShipmentDetectedModal && (
          <ModalShipmentInformationDetected
            trackEvent={trackEvent}
            isBusiness={isBusiness}
            intl={intl}
            isOpen={showShipmentDetectedModal}
            onClose={this.onModalShipmentInformationDetectedModalClose}
            onRestoreHandler={this.onRestoreHandlerShipmentDetectedModal}
          />
        )}
        <GlobalPrintStyle />

        <OverAllWrapper stepperEnabled={stepperEnabled}>
          <ResponsiveRender
            render={(isMobile) =>
              !isMobile && (
                <React.Fragment>
                  {stepperEnabled && (
                    <StepperCard
                      steps={steps}
                      currentStep={currentStep}
                      ariaDescription={ariaDescription}
                      getStepStatus={getStepStatus}
                      getIllustration={getIllustration}
                      stepNumber={stepNumber}
                      currentPage={currentPage}
                      summary={summary}
                      getCopy={this.getCopy}
                      COPY_ID_PREFIX_SHIPMENT_SUMMARY={
                        COPY_ID_PREFIX_SHIPMENT_SUMMARY
                      }
                      getCountryValue={this.getCountryValue}
                      importToggleEnabled={importToggleEnabled}
                      originFields={originFields}
                      destinationFields={destinationFields}
                      productItems={productItems}
                      formatProductDetails={formatProductDetails}
                      checkIcon={checkIcon}
                      moveToStep={(stepValue) => {
                        this.moveToStep(stepValue);
                      }}
                    />
                  )}
                </React.Fragment>
              )
            }
          />

          <ResponsiveRender
            render={(isMobile) =>
              isMobile &&
              stepperEnabled && (
                <StepperIndicator className="mx-wrapper">
                  {this.getCopy(
                    "step_mobile",
                    undefined,
                    COPY_ID_PREFIX_STEPPER
                  )}{" "}
                  {getCurrentStep()}{" "}
                  {this.getCopy("of_mobile", undefined, COPY_ID_PREFIX_STEPPER)}{" "}
                  {totalSteps}
                </StepperIndicator>
              )
            }
          />

          {!hideSegments &&
            ((stepperEnabled && currentPage === "TradeLane") ||
            !stepperEnabled ? (
              <div className="mx-wrapper">
                <div className="grid-maxWidthInner no-padding">
                  <StyledHeadline
                    stepperEnabled={stepperEnabled}
                    priority={2}
                    headlineDisplayType="h"
                    dataTestid="shipping-as-headline"
                  >
                    <SkeletonOr width={300}>
                      {this.getCopy("headline")}
                    </SkeletonOr>
                  </StyledHeadline>
                  <SegmentControl
                    segments={this.segments}
                    onClickHandler={(segment) =>
                      onSegmentChange(segment, country)
                    }
                    selectedSegment={selectedSegment}
                    segmentReference={segmentReference}
                    dataTrackingKeyPath={this.selectedSegmentTrackingKeyPath}
                  />
                </div>
              </div>
            ) : null)}

          {/* Tradelane */}

          {((stepperEnabled && currentPage === "TradeLane") ||
            !stepperEnabled) && (
            <React.Fragment>
              <this.TradeLaneWithCountry
                isTradeLaneVisible
                selectedSegment={selectedSegment}
                intl={intl}
                language={language}
                backToTradeLaneReference={this.backToTradeLaneReference}
                isBusiness={isBusiness}
                showOriginBillingLink={
                  globalConfiguration.tradeLane.showOriginBillingLink
                }
                warRestrictedZones={
                  globalConfiguration.tradeLane.warRestrictedZones
                }
                onDescribeShipmentClick={() => {
                  this.setState({
                    currentPage: "ShipmentLane",
                  });
                }}
                isOriginFlipped={(value) => {
                  this.setState({ importToggleEnabled: value === "IMPORT" });
                }}
                iNeedTheseProps={[
                  "blacklistedDestinationCountries",
                  "enableImportExportFeature",
                  "isDomesticShippingAvailable",
                  "showTradeLaneBusinessShipmentAssistant",
                  "swapCityPostalCode",
                  "changeCountryURI",
                  "changeCountryURIBusiness",
                  "hasResidencialAddress",
                ]}
                countryAutocompleteIncludeCountryCodes
                displayCountrySuggestionOnFocus
              />
            </React.Fragment>
          )}

          {/* Shipmentlane */}
          {((stepperEnabled && currentPage === "ShipmentLane") ||
            !stepperEnabled) && (
            <Suspense fallback="">
              <this.ShipmentLaneWithCountry
                abTestRunning={abTestRunning}
                intl={intl}
                stepperEnabled={stepperEnabled}
                offersLaneReference={this.OffersLaneReference}
                backToTradeLaneReference={this.backToTradeLaneReference}
                backToShipmentLaneReference={this.backToShipmentLaneReference}
                segmentReference={segmentReference}
                isBusiness={isBusiness}
                onMoveToOffersClick={() => {
                  this.setState({
                    currentPage: "OffersLane",
                  });
                }}
                hasBackButtonClicked={() => {
                  this.handleBack();
                }}
                iNeedTheseProps={[
                  "spotShipmentPresetSizes",
                  "unitSystem",
                  "shipmentLaneCheckboxStatusWeightThreshold",
                ]}
              />
            </Suspense>
          )}

          {/* Offerslane */}
          {((stepperEnabled && currentPage === "OffersLane") ||
            !stepperEnabled) && (
            <React.Fragment>
              <Suspense fallback="">
                <this.OffersLaneWithCountry
                  reference={this.OffersLaneReference}
                  backToTradeLaneReference={this.backToTradeLaneReference}
                  backToShipmentLaneReference={this.backToShipmentLaneReference}
                  iNeedTheseProps={[
                    "showCurrencyIsoCodeForPrices",
                    "aggregatedOfferViewMode",
                  ]}
                  intl={intl}
                  enableTeaserOverlayFeature={
                    globalConfiguration.offersLane[selectedSegment]
                      .enableTeaserOverlayFeature
                  }
                  isBusiness={isBusiness}
                  language={language}
                  hasBackButtonClicked={() => {
                    this.handleBack();
                  }}
                />
                {!stepperEnabled && isByPassBlockVisible && (
                  <BypassBlockWrapper className="grid-maxWidth">
                    <BypassBlock
                      dataTestid="back-to-from"
                      copyString={this.getCopy("fromTo")}
                      reference={this.backToTradeLaneReference}
                      showCopyInside
                      name="bypass-back-to-from"
                      utfDataObject={{
                        name: UTF_EVENT_CONTENT_NAMES.BACK_TO,
                        type: UTF_EVENT_TYPES.BUTTON,
                        interaction: UTF_EVENT_INTERACTIONS.CLICK,
                        position: UTF_EVENT_CONTENT_POSITION.GLOBAL,
                        context: isBusiness
                          ? SEGMENT_TYPES.business
                          : SEGMENT_TYPES.private,
                        attributes: {
                          back_to: UTF_EVENT_PAGE_NAMES.TRADE_LANE,
                        },
                      }}
                    />
                    <BypassBlock
                      dataTestid="back-to-shipment"
                      copyString={this.getCopy("shipmentSpecification")}
                      reference={this.backToShipmentLaneReference}
                      showCopyInside
                      name="bypass-back-to-shipment"
                      utfDataObject={{
                        name: UTF_EVENT_CONTENT_NAMES.BACK_TO,
                        type: UTF_EVENT_TYPES.BUTTON,
                        interaction: UTF_EVENT_INTERACTIONS.CLICK,
                        position: UTF_EVENT_CONTENT_POSITION.GLOBAL,
                        context: isBusiness
                          ? SEGMENT_TYPES.business
                          : SEGMENT_TYPES.private,
                        attributes: {
                          back_to: UTF_EVENT_PAGE_NAMES.SHIPMENT_LANE,
                        },
                      }}
                    />
                  </BypassBlockWrapper>
                )}
              </Suspense>
            </React.Fragment>
          )}
        </OverAllWrapper>
      </React.Fragment>
    );
  }
}

export default injectIntl(SpotShipment);
